import { useState } from "react";
import {
	type Feature,
	type FeatureFlagUser,
	useFeatures,
	useToggleUserFeature,
} from "../hooks/useFeatures";

interface UserFeaturePanelProps {
	user: FeatureFlagUser;
	onClose: () => void;
}

interface FeatureToggleRowProps {
	feature: Feature;
	isEnabled: boolean;
	isEnabledForAll: boolean;
	isToggling: boolean;
	onToggle: (featureKey: string, enabled: boolean) => void;
}

function FeatureToggleRow({
	feature,
	isEnabled,
	isEnabledForAll,
	isToggling,
	onToggle,
}: FeatureToggleRowProps) {
	const handleToggle = () => {
		if (!isEnabledForAll) {
			onToggle(feature.featureKey, !isEnabled);
		}
	};

	return (
		<div className="flex items-center justify-between border-b border-gray-700 py-3 last:border-b-0">
			<div className="flex-1">
				<div className="flex items-center gap-2">
					<span className="font-mono text-sm font-medium text-white">
						{feature.featureKey}
					</span>
					{isEnabledForAll && (
						<span className="rounded-full bg-green-500/20 px-2 py-0.5 text-xs font-medium text-green-400">
							Global
						</span>
					)}
				</div>
				<p className="text-sm text-gray-400">{feature.name}</p>
			</div>
			<button
				type="button"
				onClick={handleToggle}
				disabled={isToggling || isEnabledForAll}
				className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
					isEnabled || isEnabledForAll
						? "bg-indigo-600"
						: "bg-gray-600"
				} ${isEnabledForAll ? "cursor-not-allowed opacity-70" : ""}`}
				title={
					isEnabledForAll
						? "This feature is enabled for all users globally"
						: isEnabled
							? "Click to disable"
							: "Click to enable"
				}
			>
				<span
					className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
						isEnabled || isEnabledForAll
							? "translate-x-5"
							: "translate-x-0.5"
					}`}
				/>
			</button>
		</div>
	);
}

export function UserFeaturePanel({ user, onClose }: UserFeaturePanelProps) {
	const { data: featuresData, isLoading: featuresLoading } = useFeatures();
	const toggleUserFeature = useToggleUserFeature();

	// Track pending toggles to show optimistic UI
	const [pendingToggles, setPendingToggles] = useState<
		Map<string, boolean>
	>(new Map());

	// Build a map of user's current feature states
	const userFeatureMap = new Map<string, boolean>();
	if (user.featureAssignments) {
		for (const assignment of user.featureAssignments) {
			userFeatureMap.set(assignment.featureKey, assignment.enabled);
		}
	}

	const handleToggle = async (featureKey: string, enabled: boolean) => {
		// Optimistic UI update
		setPendingToggles((prev) => new Map(prev).set(featureKey, enabled));

		try {
			const result = await toggleUserFeature.mutateAsync({
				userEmails: [user.email],
				featureKey,
				enabled,
			});

			if (result.userErrors && result.userErrors.length > 0) {
				// Revert optimistic update on error
				setPendingToggles((prev) => {
					const next = new Map(prev);
					next.delete(featureKey);
					return next;
				});
				console.error("Failed to toggle feature:", result.userErrors);
			} else {
				// Success - clear pending state (actual state will come from refetch)
				setPendingToggles((prev) => {
					const next = new Map(prev);
					next.delete(featureKey);
					return next;
				});
			}
		} catch (err) {
			// Revert optimistic update on error
			setPendingToggles((prev) => {
				const next = new Map(prev);
				next.delete(featureKey);
				return next;
			});
			console.error("Failed to toggle feature:", err);
		}
	};

	const features = featuresData?.edges.map((edge) => edge.node) ?? [];

	// Determine effective enabled state for each feature (considering pending toggles)
	const getEffectiveEnabled = (featureKey: string): boolean => {
		if (pendingToggles.has(featureKey)) {
			return pendingToggles.get(featureKey) ?? false;
		}
		return userFeatureMap.get(featureKey) ?? false;
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="card max-h-[80vh] w-full max-w-lg overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between border-b border-gray-700 pb-4">
					<div>
						<h3 className="text-lg font-semibold text-white">
							Manage Features
						</h3>
						<p className="text-sm text-gray-400">{user.email}</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="text-gray-400 hover:text-white"
						aria-label="Close panel"
					>
						<svg
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={1.5}
						>
							<title>Close</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Feature list */}
				<div className="mt-4 max-h-[60vh] overflow-y-auto">
					{featuresLoading ? (
						<div className="py-8 text-center text-gray-400">
							Loading features...
						</div>
					) : features.length === 0 ? (
						<div className="py-8 text-center text-gray-400">
							No features defined yet.
						</div>
					) : (
						<div>
							{features.map((feature) => (
								<FeatureToggleRow
									key={feature.id}
									feature={feature}
									isEnabled={getEffectiveEnabled(feature.featureKey)}
									isEnabledForAll={feature.enabledForAll}
									isToggling={
										toggleUserFeature.isPending &&
										pendingToggles.has(feature.featureKey)
									}
									onToggle={handleToggle}
								/>
							))}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="mt-4 flex justify-end border-t border-gray-700 pt-4">
					<button type="button" onClick={onClose} className="btn-secondary">
						Close
					</button>
				</div>
			</div>
		</div>
	);
}
